import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  isOlarkReady,
  showOlarkBox,
  useLoadOlark,
  useOlarkStore,
} from '@fv/client-components'

import { OlarkChat } from '../olark/OlarkChat'
import { useChatSkipIntroPref } from './hooks'

export type WelcomeScreenView = 'intro' | 'quick'

type OpenLiveChatOptions = {
  shouldClosePanel?: boolean
}

type ChatPanelContextValue = {
  activeChatId?: string
  beginChatSession: (chatId: string, message: string) => void
  closePanel: () => void
  consumeInitialMessage: (chatId: string) => string | undefined
  isPanelOpen: boolean
  openLiveChat: (options?: OpenLiveChatOptions) => void
  openPanel: () => void
  selectChat: (id: string | undefined) => void
  showWelcomeIntro: () => void
  showWelcomeScreen: () => void
  togglePanel: () => void
  welcomeScreenView: WelcomeScreenView
}

const ChatPanelContext = createContext<ChatPanelContextValue | undefined>(
  undefined,
)

export const ChatPanelProvider = ({ children }: PropsWithChildren) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [activeChatId, setActiveChatId] = useState<string>()
  const [skipIntroPref, setSkipIntroPref] = useChatSkipIntroPref()
  const [welcomeScreenView, setWelcomeScreenView] = useState<WelcomeScreenView>(
    skipIntroPref ? 'quick' : 'intro',
  )
  const wasPanelOpenRef = useRef(isPanelOpen)
  const pendingInitialMessagesRef = useRef(new Map<string, string>())
  const { isEnabled, loadOlark } = useLoadOlark({ mode: 'floating' })
  const { update } = useOlarkStore(s => s.actions)

  useEffect(() => {
    if (isPanelOpen && !wasPanelOpenRef.current) {
      setWelcomeScreenView(skipIntroPref ? 'quick' : 'intro')
    }

    wasPanelOpenRef.current = isPanelOpen
  }, [isPanelOpen, skipIntroPref])

  const consumeInitialMessage = useCallback((chatId: string) => {
    const message = pendingInitialMessagesRef.current.get(chatId)
    if (!message) return undefined

    pendingInitialMessagesRef.current.delete(chatId)
    return message
  }, [])

  const resetActiveChat = useCallback(() => {
    setActiveChatId(undefined)
    pendingInitialMessagesRef.current.clear()
  }, [])

  const showWelcomeIntro = useCallback(() => {
    setSkipIntroPref(false)
    setWelcomeScreenView('intro')
  }, [setSkipIntroPref])

  const showWelcomeScreen = useCallback(() => {
    showWelcomeIntro()
    resetActiveChat()
  }, [showWelcomeIntro, resetActiveChat])

  const beginChatSession = useCallback((chatId: string, message: string) => {
    pendingInitialMessagesRef.current.set(chatId, message)
    setActiveChatId(chatId)
  }, [])

  const openPanel = useCallback(() => setIsPanelOpen(true), [])
  const closePanel = useCallback(() => setIsPanelOpen(false), [])
  const togglePanel = useCallback(() => setIsPanelOpen(prev => !prev), [])

  const openLiveChat = useCallback(
    (options?: OpenLiveChatOptions) => {
      const shouldClosePanel = options?.shouldClosePanel ?? true

      const showOlark = () => {
        showOlarkBox()
        update({ isOlarkBoxOpen: true })
        if (shouldClosePanel) {
          setIsPanelOpen(false)
        }
      }

      if (!isEnabled) {
        if (shouldClosePanel) {
          setIsPanelOpen(false)
        }
        return
      }

      if (isOlarkReady()) {
        showOlark()
        return
      }

      void loadOlark({
        onReady: () => {
          showOlark()
        },
      })
    },
    [isEnabled, loadOlark, update],
  )

  const selectChat = useCallback((id: string | undefined) => {
    setActiveChatId(id)
    if (id) {
      for (const chatId of pendingInitialMessagesRef.current.keys()) {
        if (chatId !== id) {
          pendingInitialMessagesRef.current.delete(chatId)
        }
      }
    }
  }, [])

  const value = useMemo<ChatPanelContextValue>(
    () => ({
      activeChatId,
      beginChatSession,
      closePanel,
      consumeInitialMessage,
      isPanelOpen,
      openLiveChat,
      openPanel,
      selectChat,
      showWelcomeIntro,
      showWelcomeScreen,
      togglePanel,
      welcomeScreenView,
    }),
    [
      activeChatId,
      beginChatSession,
      closePanel,
      consumeInitialMessage,
      isPanelOpen,
      openLiveChat,
      openPanel,
      selectChat,
      showWelcomeIntro,
      showWelcomeScreen,
      togglePanel,
      welcomeScreenView,
    ],
  )

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
      <OlarkChat />
    </ChatPanelContext.Provider>
  )
}

export const useChatPanel = () => {
  const context = useContext(ChatPanelContext)
  if (!context) {
    throw new Error('useChatPanel must be used within ChatPanelProvider')
  }
  return context
}
